import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaRobot, FaMagic, FaCheck, FaMapMarkerAlt, FaBolt, FaArrowRight } from 'react-icons/fa';

const MagicPreview = () => {
  const [siteData, setSiteData] = useState({
    name: "Votre Entreprise",
    city: "Votre Ville",
    tagline: "L'excellence au service de vos clients",
    description: "Nous proposons des services de qualité supérieure adaptés à vos besoins.",
    color: "#7000FF",
    theme: "modern"
  });

  const [messages, setMessages] = useState([
    { id: 1, sender: 'bot', text: "Bonjour ! Je suis l'IA d'Olney Tech. Je peux générer votre site en 30 secondes. Quel est le nom de votre entreprise ?" }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [step, setStep] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMsg = { id: Date.now(), sender: 'user', text: inputValue };
    setMessages(prev => [...prev, userMsg]);
    const currentInput = inputValue;
    setInputValue("");
    setIsTyping(true);

    setTimeout(() => {
      let botResponse = "";
      
      switch (step) {
        case 0: // Nom -> Ville
          setSiteData(prev => ({ ...prev, name: currentInput }));
          setMessages(prev => [...prev, { id: Date.now(), sender: 'bot', text: `Enchanté ! Pour optimiser votre référencement local, dans quelle ville êtes-vous situé ?` }]);
          setStep(1);
          break;

        case 1: // Ville -> Simulation
          setSiteData(prev => ({ ...prev, city: currentInput }));
          setMessages(prev => [...prev, { id: Date.now(), sender: 'bot', text: `Parfait, je scanne le web pour ${siteData.name} à ${currentInput}...` }]);
          
          setTimeout(() => {
            setMessages(prev => [...prev, { id: Date.now() + 10, sender: 'bot', text: `📍 Localisation détectée. Analyse de la concurrence locale...` }]);
            
            setTimeout(() => {
               const colors = ["#7000FF", "#00C6FF", "#FF00E5", "#00FF94"];
               const randomColor = colors[Math.floor(Math.random() * colors.length)];
               
               setSiteData(prev => ({ 
                ...prev, 
                color: randomColor,
                tagline: `Le n°1 à ${currentInput}`,
                description: `Les habitants de ${currentInput} font confiance à ${siteData.name} pour son expertise et sa réactivité.`
              }));

              setMessages(prev => [...prev, { id: Date.now() + 20, sender: 'bot', text: `✨ J'ai généré une structure optimisée pour votre marché local. Regardez à droite !` }]);
              setStep(3);
            }, 1500);
          }, 1500);
          break;

        case 3:
          botResponse = "Ce n'est qu'un début ! Avec notre Pack Lancement, nous personnalisons tout cela pour vous. Intéressé ?";
          setStep(4);
          break;

        default:
          botResponse = "Cliquez sur 'Je veux ce site' pour concrétiser ce projet !";
      }

      if (step !== 1) {
        setMessages(prev => [...prev, { id: Date.now() + 1, sender: 'bot', text: botResponse }]);
      }
      setIsTyping(false);
    }, 1000);
  };

  return (
    <section id="magic-preview" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[var(--primary)] rounded-full blur-[150px] opacity-10 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block mb-4 px-4 py-1 rounded-full border border-[var(--accent)] bg-[rgba(255,0,229,0.1)] text-[var(--accent)] text-sm font-bold tracking-widest uppercase"
          >
            Démonstration IA
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6 font-['Space_Grotesk'] text-white"
          >
            Votre Site en <span className="text-gradient-electric">Temps Réel</span>
          </motion.h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Ne imaginez plus votre site. Voyez-le. Discutez avec notre IA et regardez votre présence en ligne se construire instantanément.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 h-[650px]">
          
          {/* CHATBOT */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-panel rounded-3xl flex flex-col overflow-hidden border-[var(--primary)]/30 shadow-[0_0_50px_rgba(112,0,255,0.15)]"
          >
            <div className="p-4 bg-white/5 border-b border-white/10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] flex items-center justify-center text-white shadow-lg">
                <FaRobot />
              </div>
              <div>
                <div className="font-bold text-white">Assistant Olney</div>
                <div className="text-xs text-[var(--secondary)] flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span> En ligne
                </div>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
              {messages.map((msg) => (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  key={msg.id} 
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] p-4 rounded-2xl ${
                    msg.sender === 'user' 
                      ? 'bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-white rounded-tr-none shadow-lg' 
                      : 'bg-white/10 text-gray-200 rounded-tl-none border border-white/5'
                  }`}>
                    {msg.text}
                  </div>
                </motion.div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white/10 text-gray-200 p-4 rounded-2xl rounded-tl-none flex gap-2 border border-white/5">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10 bg-white/5">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Écrivez votre message..."
                  className="flex-1 bg-[#030014] border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[var(--primary)] transition-colors placeholder-gray-600"
                />
                <button 
                  type="submit"
                  className="bg-[var(--primary)] hover:bg-[var(--primary)]/80 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-purple-500/20"
                >
                  <FaBolt />
                </button>
              </div>
            </form>
          </motion.div>

          {/* LIVE PREVIEW */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="relative rounded-3xl overflow-hidden border-4 border-gray-800 bg-white shadow-2xl flex flex-col"
          >
            {/* Fake Browser Bar */}
            <div className="bg-gray-100 border-b border-gray-200 p-3 flex items-center gap-3">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <div className="flex-1 bg-white rounded-md h-8 px-3 text-xs flex items-center text-gray-500 shadow-sm border border-gray-200">
                <FaCheck className="text-green-500 mr-2" /> https://{siteData.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.fr
              </div>
            </div>

            {/* Website Content */}
            <div className="flex-1 overflow-y-auto bg-white text-gray-900 relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step} // Re-render on step change for animation
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="h-full"
                >
                  {/* Navbar */}
                  <nav className="p-5 flex justify-between items-center border-b border-gray-100 sticky top-0 bg-white/90 backdrop-blur-sm z-10">
                    <div className="font-bold text-xl flex items-center gap-2" style={{ color: siteData.color }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: siteData.color }}>
                        {siteData.name.charAt(0)}
                      </div>
                      {siteData.name}
                    </div>
                    <div className="hidden sm:flex gap-6 text-sm font-medium text-gray-600">
                      <span className="cursor-pointer hover:text-black">Accueil</span>
                      <span className="cursor-pointer hover:text-black">Services</span>
                      <span className="cursor-pointer hover:text-black">Contact</span>
                    </div>
                  </nav>

                  {/* Hero */}
                  <div className="px-8 py-16 text-center bg-gray-50 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full opacity-5" style={{ backgroundColor: siteData.color }}></div>
                    <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900 leading-tight">
                      {siteData.tagline}
                    </h1>
                    <p className="text-gray-600 mb-10 max-w-lg mx-auto text-lg">
                      {siteData.description}
                    </p>
                    <div className="flex justify-center gap-4">
                      <button 
                        className="text-white px-8 py-3 rounded-full font-bold shadow-lg transform hover:scale-105 transition-transform flex items-center gap-2"
                        style={{ backgroundColor: siteData.color }}
                      >
                        Nous Contacter <FaArrowRight />
                      </button>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="p-8 grid grid-cols-2 gap-6">
                    <div className="p-6 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                      <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center text-white text-xl shadow-md" style={{ backgroundColor: siteData.color }}>
                        <FaMapMarkerAlt />
                      </div>
                      <h3 className="font-bold text-lg mb-2">Expert Local</h3>
                      <p className="text-sm text-gray-500">Basé à {siteData.city}, nous connaissons vos besoins.</p>
                    </div>
                    <div className="p-6 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                      <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center text-white text-xl shadow-md" style={{ backgroundColor: siteData.color }}>
                        <FaMagic />
                      </div>
                      <h3 className="font-bold text-lg mb-2">Sur Mesure</h3>
                      <p className="text-sm text-gray-500">Une solution adaptée à votre activité.</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Overlay "Acheter ce site" */}
              {step >= 3 && (
                <motion.div 
                  initial={{ y: 100 }}
                  animate={{ y: 0 }}
                  className="absolute bottom-6 left-0 right-0 flex justify-center z-20"
                >
                  <a href="#contact" className="bg-[#00FF94] hover:bg-[#00cc76] text-[#030014] px-8 py-4 rounded-full font-bold shadow-[0_10px_30px_rgba(0,255,148,0.4)] animate-bounce flex items-center gap-2 transform hover:scale-105 transition-all">
                    <FaCheck /> Je veux ce site !
                  </a>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MagicPreview;
